import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import { useQuery } from '@apollo/client/react'
import { formatISO } from 'date-fns'
import showDate, { isSameDate } from 'helpers/date/showDate'

import TabMenu from 'js/components/Navigation/TabMenu'
import TabPanel from 'js/components/Navigation/TabPanel'
import groupActivitiesByDate from 'js/lib/helpers/groupActivitiesByDate'
import { onlyTypename } from 'js/lib/helpers/onlyTypename'

import { Activity } from '../components/Activity'
import ActivityActions from '../components/ActivityActions'
import type { EventItemQuery } from '../EventPage/EventItem.queries.generated'
import { EventItemViewFragment } from '../EventPage/EventItemView.fragment.generated'
import type { EventCoreFragment } from '../fragments/EventCore.fragment.generated'

import { EventChildFragmentFragment } from './EventChildFragment.fragment.generated'
import { EVENT_ITEM_CHILD_DETAILS } from './EventItemWithDetailedChildren.queries'

type BaseEvent = EventItemViewFragment['children'][0]

type Props = {
    guid: string
    isDisabled: boolean

    /**
     * Our main page requests the minimal data about its sub events (activities).
     *
     * Inside our component we will load all the details, so our page renders fast
     * because on first render it knows how many items and on which dates.
     * After the detailed data `EVENT_ITEM_CHILD_DETAILSis loaded, the component
     * will render the full information about each activity.
     */
    summaryEvents: BaseEvent[] | null

    // FIXME: viewer should not be passed trough everything
    viewer: EventItemQuery['viewer']
    // TODO: same for refetching, component level should tell what to do
    refetchQueries: any
} & Pick<EventCoreFragment, 'startDate' | 'endDate'>

/**
 * The sub events (activities) of an event, grouped by date.
 * the basic information is given with `summaryEvents` for our first paint
 */
export const EventActivities = ({
    guid,
    viewer,
    refetchQueries,
    isDisabled,
    startDate,
    endDate,
    summaryEvents,
}: Props) => {
    const { t } = useTranslation()
    const { hash } = useLocation()

    const { data } = useQuery(EVENT_ITEM_CHILD_DETAILS, {
        variables: { guid },
        fetchPolicy: 'cache-and-network',
    })

    const detailedChildren = onlyTypename(data?.entity, 'Event')?.children
    const activities = detailedChildren ?? summaryEvents ?? []

    const activitiesByDate = groupActivitiesByDate<
        EventChildFragmentFragment | BaseEvent
    >(activities)

    if (activitiesByDate.length === 0) {
        return null
    }

    const activitiesByHash =
        hash &&
        activitiesByDate.find((el) => {
            return Object.keys(el)[0] === hash.substring(1)
        })

    const activeHashActivities = activitiesByHash
        ? Object.values(activitiesByHash)?.at(0)
        : Object.values(activitiesByDate.at(0))?.at(0)

    // Show date tabs if there are one or more activities spread over multiple days
    const showActivityDateTabs =
        activitiesByDate.length > 1 ||
        (activitiesByDate.length === 1 &&
            (!isSameDate(activities[0].startDate, startDate) ||
                !isSameDate(activities[0].startDate, endDate)))

    return (
        <div style={{ marginTop: '24px' }}>
            {showActivityDateTabs && (
                <TabMenu
                    label={t('entity-event.activities')}
                    showBorder
                    options={activitiesByDate.map((activity, index) => {
                        const key = Object.keys(activity)[0]
                        const hashIsKey = hash === `#${key}`
                        const { guid } = Object.values(activity)[0][0]

                        return {
                            id: `tab-${guid}`,
                            label: showDate(formatISO(key), false, true),
                            link: `#${key}`,
                            isActive:
                                index === 0 ? !hash || hashIsKey : hashIsKey,
                            ariaControls: `tabpanel-${guid}`,
                        }
                    })}
                    style={{ margin: '8px 0 16px' }}
                />
            )}

            <div>
                {activeHashActivities?.map((activity, i) => (
                    <TabPanel
                        key={activity.guid}
                        id={`tabpanel-${activity.guid}`}
                        aria-labelledby={`tab-${activity.guid}`}
                        visible
                    >
                        <Activity
                            entity={activity}
                            showSlots
                            showSpots
                            isFirst={i === 0}
                            isLast={i === activeHashActivities.length - 1}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    paddingRight: '4px',
                                }}
                            >
                                <ActivityActions
                                    entity={activity}
                                    viewer={viewer}
                                    refetchQueries={refetchQueries}
                                    disabled={isDisabled}
                                />
                            </div>
                        </Activity>
                    </TabPanel>
                ))}
            </div>
        </div>
    )
}
